import React, { useCallback, useMemo } from 'react'
import { AlertFilled } from '@ant-design/icons'
import { Popconfirm } from 'antd'
import PropTypes from 'prop-types'

import { GridFormItem } from '../../../core/components/sections'

import { StyledPaddingBottomRow } from '../../../common/components/styled'
import { PERMISSIONS, ROLES } from '../../../common/constants'

import { useUserHasPermissions, useUserHasRoles } from '../../../account/hooks'
import {
  useRetryMenuCurationTaskAutomaticCurationMutation,
  useRetryMenuCurationTaskLabelsAndIngredientsMutation,
  useUnlockMenuCurationTaskDishesMutation,
} from '../../hooks'
import { StyledRetryButton } from '../styled'

const MenuCurationTaskRetryButtons = ({
  menuCurationTaskId,
  hasLockedDishes,
  isDisabled,
}) => {
  const [retryAutomaticCuration] =
    useRetryMenuCurationTaskAutomaticCurationMutation()
  const [retryLabelsAndIngredients] =
    useRetryMenuCurationTaskLabelsAndIngredientsMutation()
  const [unlockDishes] = useUnlockMenuCurationTaskDishesMutation()

  const hasPermissionToRetryCuration = useUserHasPermissions(
    PERMISSIONS.MENU_CURATION_TASK.RETRY_CURATION,
  )

  const hasPermissionToUnlockDishes = useUserHasPermissions(
    PERMISSIONS.MENU_CURATION_TASK.UNLOCK_DISHES,
  )

  const userCanRetryAnyTask = useUserHasRoles([
    ROLES.ADMIN.value,
    ROLES.GUILD_LEAD.value,
  ])

  const areRetryButtonsDisabled = useMemo(
    () => (isDisabled || !hasPermissionToRetryCuration) && !userCanRetryAnyTask,
    [hasPermissionToRetryCuration, isDisabled, userCanRetryAnyTask],
  )

  const handleRetryAutomaticCuration = useCallback(() => {
    retryAutomaticCuration(menuCurationTaskId)
  }, [menuCurationTaskId, retryAutomaticCuration])

  const handleRetryLabelsAndIngredients = useCallback(() => {
    retryLabelsAndIngredients(menuCurationTaskId)
  }, [menuCurationTaskId, retryLabelsAndIngredients])

  const handleUnlockDishes = useCallback(() => {
    unlockDishes(menuCurationTaskId)
  }, [menuCurationTaskId, unlockDishes])

  return (
    <StyledPaddingBottomRow gutter={16}>
      <GridFormItem span={4} label="Automatic Curation">
        <Popconfirm
          disabled={areRetryButtonsDisabled}
          onConfirm={handleRetryAutomaticCuration}
          title={`
            This will unassign ALL users, remove done flags and delete ALL previous curation data for ALL dishes and get new data. Are you sure?
            You will be Unassigned from this task!`}
          icon={<AlertFilled style={{ color: 'red' }} />}
        >
          <StyledRetryButton disabled={areRetryButtonsDisabled}>
            Retry all curation
          </StyledRetryButton>
        </Popconfirm>
      </GridFormItem>
      <GridFormItem span={4} label="Labels and ingredients">
        <Popconfirm
          disabled={areRetryButtonsDisabled}
          onConfirm={handleRetryLabelsAndIngredients}
          title="This will unassign ALL users, remove done flags and delete ALL previous labels and ingredients on dishes and get new ones. Are you sure? You will be Unassigned from this task!"
          icon={<AlertFilled style={{ color: 'red' }} />}
        >
          <StyledRetryButton disabled={areRetryButtonsDisabled}>
            Retry labels and ingredients
          </StyledRetryButton>
        </Popconfirm>
      </GridFormItem>
      {hasPermissionToUnlockDishes && hasLockedDishes && (
        <GridFormItem span={4} label="Unlock dishes">
          <Popconfirm
            disabled={areRetryButtonsDisabled}
            onConfirm={handleUnlockDishes}
            title="Are you sure you want to unlock all dishes?"
            icon={<AlertFilled style={{ color: 'red' }} />}
          >
            <StyledRetryButton>Unlock dishes</StyledRetryButton>
          </Popconfirm>
        </GridFormItem>
      )}
    </StyledPaddingBottomRow>
  )
}

MenuCurationTaskRetryButtons.propTypes = {
  menuCurationTaskId: PropTypes.string.isRequired,
  isDisabled: PropTypes.bool,
  hasLockedDishes: PropTypes.bool,
}

MenuCurationTaskRetryButtons.defaultProps = {
  isDisabled: false,
  hasLockedDishes: false,
}

export default MenuCurationTaskRetryButtons
